import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AuthorBox from "../components/AuthorBox";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  ArrowRight, 
  Target, 
  BarChart3, 
  Settings, 
  Database,
  Users,
  LineChart,
  CheckCircle,
  Zap,
  Shield,
  TrendingUp,
  Calendar,
  Award,
  Building2,
  Factory,
  Briefcase,
  HeartHandshake
} from "lucide-react";

const KIValueEngine = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();

  // SEO Data with FAQs for GEO
  const seoData = {
    title: currentLanguage === 'de' 
      ? 'KI-Value-Engine | Das Betriebsmodell für messbare KI-Transformation - BROVKO'
      : 'AI Value Engine | The Operating Model for Measurable AI Transformation - BROVKO',
    description: currentLanguage === 'de'
      ? 'Die KI-Value-Engine: Von der Probierstube zur steuerbaren Wertmaschine. Portfolio-Steuerung, Value-Dashboard und Operating Rhythm für EBIT-Impact. 6 Bausteine für messbare KI-Erfolge.'
      : 'The AI Value Engine: From pilot projects to controllable value creation. Portfolio management, Value Dashboard and Operating Rhythm for EBIT impact. 6 building blocks for measurable AI success.',
    keywords: 'KI-Value-Engine, AI Value Engine, KI-Transformation, Portfolio-Steuerung, Value-Dashboard, Operating Rhythm, EBIT Impact, KI-Strategie, ROI KI, Enterprise AI',
    canonicalUrl: '/ki-value-engine',
    ogTitle: currentLanguage === 'de' ? 'KI-Value-Engine - Das Betriebsmodell für KI-Erfolg' : 'AI Value Engine - The Operating Model for AI Success',
    ogDescription: currentLanguage === 'de'
      ? 'Verwandeln Sie KI-Piloten in messbare Wertschöpfung. Die KI-Value-Engine mit 6 Bausteinen für nachhaltigen EBIT-Impact.'
      : 'Transform AI pilots into measurable value creation. The AI Value Engine with 6 building blocks for sustainable EBIT impact.',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'KI-Value-Engine', url: '/ki-value-engine' }
    ],
    faqData: [
      {
        question: 'Was ist die KI-Value-Engine?',
        answer: 'Die KI-Value-Engine ist ein Management- und Delivery-System, das KI-Initiativen als Portfolio steuert, Adoption und Impact über ein Value-Dashboard misst und Skalierung über einen festen Operating Rhythm sicherstellt. Sie transformiert KI von der Probierstube zur steuerbaren Wertmaschine.'
      },
      {
        question: 'Wie unterscheidet sich die KI-Value-Engine von anderen KI-Ansätzen?',
        answer: 'Im Gegensatz zu Einzelprojekten oder Piloten behandelt die KI-Value-Engine KI wie ein Geschäftsfeld mit klarer Portfolio-Steuerung, messbaren KPIs (Readiness, Adoption, Impact, Sentiment) und einem regelmäßigen Operating Rhythm mit Stop/Fix/Scale-Entscheidungen.'
      },
      {
        question: 'Welche 6 Bausteine hat die KI-Value-Engine?',
        answer: 'Die 6 Bausteine sind: 1) Value-Thesen & KPI-Baselines, 2) Use Case Portfolio (Quick Wins, Core Value, Strategic Bets), 3) KI-Value-Dashboard mit 12 Kern-KPIs, 4) Operating Rhythm mit monatlichen Value-Boards, 5) Governance by Design (EU AI Act ready), 6) Adoption Management & Change.'
      },
      {
        question: 'Wie lange dauert die Implementierung der KI-Value-Engine?',
        answer: 'Die KI-Value-Engine kann in 90 Tagen implementiert werden: Schritt 1 (Wochen 1-4): Portfolio-Setup mit Use-Case-Intake und Scoring. Schritt 2 (parallel): Value-Dashboard v1 mit 12 Kern-KPIs. Schritt 3 (ab Woche 4): Operating Rhythm mit monatlichen Value-Boards.'
      },
      {
        question: 'Für welche Unternehmen eignet sich die KI-Value-Engine?',
        answer: 'Die KI-Value-Engine eignet sich für Mittelständler (50-500 MA) ohne eigene Data-Science-Abteilung, PE-Portfolio-Companies vor dem Exit, und Konzernbereiche, die aus der "Pilotitis" herauskommen wollen. Entscheidend ist der Wille, KI systematisch zu steuern.'
      }
    ]
  };

  const handleValueCheck = () => {
    navigate('/ki-value-check');
  };

  const handleContact = () => {
    navigate('/contact');
  };

  // 6 Building Blocks of the Value Engine
  const engineModules = [
    {
      icon: Target,
      title: currentLanguage === 'de' ? 'Value-Thesen & KPI-Baselines' : 'Value Hypotheses & KPI Baselines',
      description: currentLanguage === 'de' 
        ? 'Für jeden Use Case: Baseline → Ziel → KPI → Owner (CFO-fähig). Outcome-KPIs statt Output-Metriken.'
        : 'For each use case: Baseline → Target → KPI → Owner (CFO-ready). Outcome KPIs instead of output metrics.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: BarChart3,
      title: currentLanguage === 'de' ? 'Portfolio & Priorisierung' : 'Portfolio & Prioritization',
      description: currentLanguage === 'de'
        ? 'Use-Case-Portfolio wie ein Investment-Portfolio: Value, Risiko, Reife, Abhängigkeiten. Quick Wins, Core Value, Strategic Bets.'
        : 'Use case portfolio like an investment portfolio: Value, risk, maturity, dependencies. Quick wins, core value, strategic bets.',
      color: 'from-brand to-green-500'
    },
    {
      icon: Factory,
      title: currentLanguage === 'de' ? 'Delivery-Fabrik (Factory)' : 'Delivery Factory',
      description: currentLanguage === 'de'
        ? 'Wiederholbare Umsetzung: Templates, Rollen, Standards, Sicherheits-/Compliance-Gates. Definition "Done" inklusive.'
        : 'Repeatable implementation: Templates, roles, standards, security/compliance gates. Definition of "Done" included.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Database,
      title: currentLanguage === 'de' ? 'Plattform & Daten (Enablement)' : 'Platform & Data (Enablement)',
      description: currentLanguage === 'de'
        ? 'Private LLM, Integration, Datenzugriff, Security – ohne Plattform wird Skalierung zäh. Auditierbarkeit als Value-Faktor.'
        : 'Private LLM, integration, data access, security – without platform, scaling becomes difficult. Auditability as value factor.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Users,
      title: currentLanguage === 'de' ? 'Adoption & Arbeitsweise' : 'Adoption & Workflows',
      description: currentLanguage === 'de'
        ? 'Enablement, Prompt-Patterns, Change – passend zur Kultur. Training als Value-Hebel. Champions und vordefinierte Prompts.'
        : 'Enablement, prompt patterns, change – matching the culture. Training as value lever. Champions and predefined prompts.',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: LineChart,
      title: currentLanguage === 'de' ? 'Value-Dashboard & Review-Cadence' : 'Value Dashboard & Review Cadence',
      description: currentLanguage === 'de'
        ? 'Monatlicher Value-Review wie ein Board-Format: Output → Outcome → €-Impact → Learnings. Stop/Fix/Scale Entscheidungen.'
        : 'Monthly value review like a board format: Output → Outcome → €-Impact → Learnings. Stop/Fix/Scale decisions.',
      color: 'from-indigo-500 to-purple-500'
    }
  ];

  // CEO Benefits
  const ceoBenefits = [
    {
      icon: TrendingUp,
      title: currentLanguage === 'de' ? 'Transparenz' : 'Transparency',
      description: currentLanguage === 'de'
        ? 'KI ist nicht mehr „Hoffnung", sondern steuerbarer Werttreiber mit klaren KPIs.'
        : 'AI is no longer "hope", but a controllable value driver with clear KPIs.'
    },
    {
      icon: Shield,
      title: currentLanguage === 'de' ? 'Investitionsschutz' : 'Investment Protection',
      description: currentLanguage === 'de'
        ? 'Weniger Tool-Aktionismus, mehr Portfolio-Logik. Fokussierte Ressourcenallokation.'
        : 'Less tool activism, more portfolio logic. Focused resource allocation.'
    },
    {
      icon: Zap,
      title: currentLanguage === 'de' ? 'Skalierung' : 'Scalability',
      description: currentLanguage === 'de'
        ? 'Aus 2 Piloten werden 20 Rollouts – ohne Chaos. Standardisierte Prozesse.'
        : 'From 2 pilots to 20 rollouts – without chaos. Standardized processes.'
    },
    {
      icon: CheckCircle,
      title: currentLanguage === 'de' ? 'Governance ohne Bremse' : 'Governance Without Slowdown',
      description: currentLanguage === 'de'
        ? 'Compliance wird eingebaut, nicht nachträglich „draufgeklebt". EU AI Act ready.'
        : 'Compliance is built in, not "glued on" afterwards. EU AI Act ready.'
    }
  ];

  // Benchmark Companies
  const benchmarks = [
    {
      company: 'Microsoft',
      fact: currentLanguage === 'de' 
        ? 'Copilot Dashboard misst Readiness, Adoption, Impact, Sentiment'
        : 'Copilot Dashboard measures Readiness, Adoption, Impact, Sentiment',
      pattern: currentLanguage === 'de'
        ? 'Value-Engine = Messsystem + Rollout-Steuerung'
        : 'Value Engine = Measurement system + Rollout control'
    },
    {
      company: 'Amazon',
      fact: currentLanguage === 'de'
        ? '1 Mio. Roboter; DeepFleet zielt auf ~10% Travel Efficiency'
        : '1M robots; DeepFleet targets ~10% Travel Efficiency',
      pattern: currentLanguage === 'de'
        ? 'KI-Wert wird als operative KPI formuliert'
        : 'AI value is formulated as operational KPI'
    },
    {
      company: 'Bosch',
      fact: currentLanguage === 'de'
        ? '>2,5 Mrd. € KI-Invest bis Ende 2027; agentic AI als Manufacturing-Hebel'
        : '>€2.5B AI investment by end of 2027; agentic AI as manufacturing lever',
      pattern: currentLanguage === 'de'
        ? 'KI ist Wachstums- und Produktionshebel'
        : 'AI is growth and production lever'
    },
    {
      company: 'Siemens',
      fact: currentLanguage === 'de'
        ? 'Über 100 Kunden in EU/USA nutzen den Industrial Copilot'
        : 'Over 100 customers in EU/USA use the Industrial Copilot',
      pattern: currentLanguage === 'de'
        ? 'Industrialisierte KI mit Domain-Know-how + Plattform'
        : 'Industrialized AI with domain expertise + platform'
    }
  ];

  // 7 Mechanisms - WITH ICONS
  const mechanisms = [
    {
      number: '01',
      icon: Calendar,
      title: currentLanguage === 'de' ? 'Operating Rhythm' : 'Operating Rhythm',
      description: currentLanguage === 'de'
        ? 'KI wie ein Geschäftsfeld führen – fester Takt mit Stop/Fix/Scale.'
        : 'Manage AI like a business unit – fixed cadence with Stop/Fix/Scale.'
    },
    {
      number: '02',
      icon: Briefcase,
      title: currentLanguage === 'de' ? 'Portfolio statt Projektitis' : 'Portfolio Instead of Project-itis',
      description: currentLanguage === 'de'
        ? 'Use Cases als Investments: Quick Wins, Core Value, Strategic Bets.'
        : 'Use cases as investments: Quick Wins, Core Value, Strategic Bets.'
    },
    {
      number: '03',
      icon: LineChart,
      title: currentLanguage === 'de' ? 'Value-Dashboard' : 'Value Dashboard',
      description: currentLanguage === 'de'
        ? 'Readiness, Adoption, Impact, Sentiment – das Cockpit für Entscheider.'
        : 'Readiness, Adoption, Impact, Sentiment – the cockpit for decision-makers.'
    },
    {
      number: '04',
      icon: Shield,
      title: currentLanguage === 'de' ? 'Governance by Design' : 'Governance by Design',
      description: currentLanguage === 'de'
        ? 'Compliance eingebaut, nicht nachträglich. EU AI Act ready.'
        : 'Compliance built in, not added later. EU AI Act ready.'
    },
    {
      number: '05',
      icon: Database,
      title: currentLanguage === 'de' ? 'Plattform-These' : 'Platform Thesis',
      description: currentLanguage === 'de'
        ? 'Datenzugriff, Rechte, Logging als Wettbewerbsfaktor.'
        : 'Data access, rights, logging as competitive factor.'
    },
    {
      number: '06',
      icon: Settings,
      title: currentLanguage === 'de' ? 'Agent-Orchestrierung' : 'Agent Orchestration',
      description: currentLanguage === 'de'
        ? 'Orchestrierte Super-Interfaces statt Tool-Wildwuchs.'
        : 'Orchestrated super interfaces instead of tool sprawl.'
    },
    {
      number: '07',
      icon: Factory,
      title: currentLanguage === 'de' ? 'Industrial/Embodied AI' : 'Industrial/Embodied AI',
      description: currentLanguage === 'de'
        ? 'KI wird physisch – größter Hebel in Prozess-/Industrie-Integration.'
        : 'AI becomes physical – biggest lever in process/industry integration.'
    }
  ];

  // Dashboard KPI Categories
  const dashboardCategories = [
    {
      category: 'Readiness',
      kpis: currentLanguage === 'de' 
        ? ['Rollen/Owner benannt', 'Datenzugriff geregelt', 'Security/Compliance Gate definiert']
        : ['Roles/Owners named', 'Data access regulated', 'Security/Compliance Gate defined'],
      color: 'bg-blue-500'
    },
    {
      category: 'Adoption',
      kpis: currentLanguage === 'de'
        ? ['Aktive Nutzer', 'Nutzung pro Prozess', 'Wiederkehrquote']
        : ['Active users', 'Usage per process', 'Return rate'],
      color: 'bg-green-500'
    },
    {
      category: 'Impact',
      kpis: currentLanguage === 'de'
        ? ['Zeitersparnis', 'Durchsatz', 'Fehlerquote', '€-Hebel']
        : ['Time savings', 'Throughput', 'Error rate', '€ leverage'],
      color: 'bg-brand'
    },
    {
      category: 'Sentiment',
      kpis: currentLanguage === 'de'
        ? ['Nutzerzufriedenheit', 'Top-3 Hindernisse']
        : ['User satisfaction', 'Top 3 obstacles'],
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface">
      <SEOHead {...seoData} />
      <Header />

      {/* Hero Section - COMPACT */}
      <section className="relative pt-32 pb-24 bg-brand-hero overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-56 h-56 bg-brand rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-56 h-56 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-[46rem]">
            <h1 className="max-w-[14ch] text-[clamp(2.4rem,5vw,4.2rem)] font-extrabold tracking-[-0.04em] leading-[1.04] [text-wrap:balance] text-white mb-5">
              {currentLanguage === 'de' ? (
                <>
                  Die <span className="bg-brand-accent bg-clip-text text-transparent">KI-Value-Engine</span>
                </>
              ) : (
                <>
                  The <span className="bg-brand-accent bg-clip-text text-transparent">AI Value Engine</span>
                </>
              )}
            </h1>

            <p className="max-w-[52ch] text-lg sm:text-xl text-ink-muted mb-3 font-light">
              {currentLanguage === 'de'
                ? 'Von der Probierstube zur steuerbaren Wertmaschine'
                : 'From pilot projects to a controllable value engine'}
            </p>

            <p className="text-base text-ink-faint mb-8 max-w-[62ch]">
              {currentLanguage === 'de'
                ? 'Ein Management- und Delivery-System, das KI-Initiativen als Portfolio steuert und Skalierung über einen festen Operating Rhythm sicherstellt.'
                : 'A management and delivery system that manages AI initiatives as a portfolio and ensures scaling through a fixed operating rhythm.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                size="lg"
                onClick={handleValueCheck}
                className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                data-testid="hero-value-check-btn"
              >
                {currentLanguage === 'de' ? 'Kostenloser Value-Check' : 'Free Value Check'}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={handleContact}
                className="border-white/30 text-white hover:bg-white/[0.07] px-6 py-3 font-semibold rounded-lg transition-all duration-300"
                data-testid="hero-contact-btn"
              >
                {currentLanguage === 'de' ? 'Beratungsgespräch buchen' : 'Book Consultation'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - COMPACT */}
      <section className="py-20 md:py-28 bg-surface-sunken">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-10">
            <span className="inline-block bg-red-500/15 text-red-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              {currentLanguage === 'de' ? 'Das Problem' : 'The Problem'}
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-4">
              {currentLanguage === 'de'
                ? 'Warum viele KI-Transformationen scheitern'
                : 'Why Many AI Transformations Fail'}
            </h2>
            <p className="text-base text-ink-muted">
              {currentLanguage === 'de'
                ? 'Viele Organisationen haben Piloten, Demos und Einzelteams. Aber keinen Mechanismus, der beantwortet:'
                : 'Many organizations have pilots, demos, and individual teams. But no mechanism that answers:'}
            </p>
          </div>

          <div className="max-w-[62ch]">
            <div className="bg-surface rounded-xl p-6 shadow-lg border border-hairline">
              <p className="text-xl text-center text-ink font-medium italic">
                {currentLanguage === 'de'
                  ? '„Was bringt KI – wo, wieviel, bis wann – und wer ist accountable?"'
                  : '"What does AI bring – where, how much, by when – and who is accountable?"'}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-8">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">🎯</span>
                </div>
                <h3 className="font-semibold text-ink mb-1 text-sm">
                  {currentLanguage === 'de' ? 'Fehlende Steuerung' : 'Missing Control'}
                </h3>
                <p className="text-ink-muted text-xs">
                  {currentLanguage === 'de'
                    ? 'Piloten ohne Dashboard, Use Cases ohne KPIs'
                    : 'Pilots without dashboard, use cases without KPIs'}
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">📊</span>
                </div>
                <h3 className="font-semibold text-ink mb-1 text-sm">
                  {currentLanguage === 'de' ? 'Kein EBIT-Impact' : 'No EBIT Impact'}
                </h3>
                <p className="text-ink-muted text-xs">
                  {currentLanguage === 'de'
                    ? 'Nutzen auf Use-Case-Ebene, nicht auf Unternehmensebene'
                    : 'Value at use case level, not at enterprise level'}
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">🔄</span>
                </div>
                <h3 className="font-semibold text-ink mb-1 text-sm">
                  {currentLanguage === 'de' ? 'Pilotitis' : 'Pilot-itis'}
                </h3>
                <p className="text-ink-muted text-xs">
                  {currentLanguage === 'de'
                    ? 'Viele Experimente, wenig Skalierung'
                    : 'Many experiments, little scaling'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution: 6 Building Blocks - COMPACT */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-10">
            <span className="inline-block bg-brand/10 text-brand-300 border border-brand-400/25 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              {currentLanguage === 'de' ? 'Die Lösung' : 'The Solution'}
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              {currentLanguage === 'de'
                ? 'Die 6 Bausteine der KI-Value-Engine'
                : 'The 6 Building Blocks of the AI Value Engine'}
            </h2>
            <p className="text-base text-ink-muted max-w-[62ch]">
              {currentLanguage === 'de'
                ? 'Ein Betriebsmodell, das KI planbar in messbaren Nutzen übersetzt.'
                : 'An operating model that translates AI predictably into measurable value.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {engineModules.map((module, index) => (
              <div 
                key={index}
                className="group bg-surface-sunken hover:bg-surface rounded-xl p-5 border border-hairline hover:border-brand-400/25 hover:shadow-lg transition-all duration-300"
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${module.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300`}>
                  <module.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-sm font-bold text-ink mb-2 group-hover:text-brand-400 transition-colors">
                  {module.title}
                </h3>
                <p className="text-ink-muted text-xs">
                  {module.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CEO Benefits Section - COMPACT */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <span className="inline-block bg-brand/15 border border-teal-500/30 text-brand-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              {currentLanguage === 'de' ? 'CEO-Perspektive' : 'CEO Perspective'}
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
              {currentLanguage === 'de'
                ? 'Der Nutzen für die Geschäftsführung'
                : 'The Benefits for Management'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ceoBenefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-surface-raised/50 backdrop-blur border border-hairline rounded-xl p-4 hover:border-teal-500/50 transition-all duration-300"
              >
                <div className="w-10 h-10 bg-brand/15 rounded-lg flex items-center justify-center mb-3">
                  <benefit.icon className="h-5 w-5 text-brand-400" />
                </div>
                <h3 className="text-base font-bold text-white mb-1">{benefit.title}</h3>
                <p className="text-ink-faint text-xs">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 Mechanisms Section - IMPROVED WITH ICONS AND GRID */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-10">
            <span className="inline-block bg-indigo-500/15 text-indigo-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              {currentLanguage === 'de' ? 'Vorreiter-Prinzipien' : 'Pioneer Principles'}
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              {currentLanguage === 'de'
                ? '7 Mechanismen, die KI zur Value-Engine machen'
                : '7 Mechanisms That Turn AI Into a Value Engine'}
            </h2>
            <p className="text-base text-ink-muted max-w-[62ch]">
              {currentLanguage === 'de'
                ? 'Wiederholbare Management-Mechaniken statt „mehr KI-Projekte".'
                : 'Repeatable management mechanics instead of "more AI projects".'}
            </p>
          </div>

          {/* Durchnummerierte Zeilen statt eines Vierer- und eines
              Dreier-Rasters. Sieben Eintraege gehen in keinem Raster
              gleichmaessig auf, in Zeilen ist die Zahl gleichgueltig. */}
          <ol className="border-t border-hairline">
            {mechanisms.map((mechanism, index) => (
              <li
                key={index}
                className="group grid gap-3 border-b border-hairline py-7 transition-[background-color,padding] duration-300 ease-brand hover:bg-brand/[0.045] hover:pl-4 md:grid-cols-[64px_300px_1fr] md:items-baseline md:gap-8"
              >
                <span className="text-sm font-bold tabular-nums text-brand-300">
                  {mechanism.number}
                </span>
                <h3 className="text-xl font-bold tracking-[-0.02em] text-ink transition-colors group-hover:text-brand-300">
                  {mechanism.title}
                </h3>
                <p className="max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-muted">
                  {mechanism.description}
                </p>
              </li>
            ))}
          </ol>

          {/* Link to Value Dashboard */}
          <div className="text-center mt-8">
            <Link to="/value-dashboard" className="inline-flex items-center gap-2 text-brand-400 hover:text-brand-300 font-medium text-sm transition-colors">
              <LineChart className="h-4 w-4" />
              Value-Dashboard live ansehen
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Value Dashboard Section - COMPACT */}
      <section className="py-20 md:py-28 bg-surface-sunken">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <span className="inline-block bg-green-500/15 text-green-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              {currentLanguage === 'de' ? 'Das Cockpit' : 'The Cockpit'}
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              {currentLanguage === 'de'
                ? 'Das KI-Value-Dashboard'
                : 'The AI Value Dashboard'}
            </h2>
            <p className="text-base text-ink-muted max-w-[62ch]">
              {currentLanguage === 'de'
                ? 'Gemessen wird Adoption + Impact + Risiko – nicht „KI".'
                : 'What is measured is Adoption + Impact + Risk – not "AI".'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {dashboardCategories.map((cat, index) => (
              <div key={index} className="bg-surface rounded-xl p-4 shadow-lg border border-hairline">
                <div className={`w-full h-1.5 ${cat.color} rounded-full mb-3`}></div>
                <h3 className="text-base font-bold text-ink mb-2">{cat.category}</h3>
                <ul className="space-y-1.5">
                  {cat.kpis.map((kpi, kpiIndex) => (
                    <li key={kpiIndex} className="flex items-start gap-1.5 text-ink-muted text-xs">
                      <CheckCircle className="h-3 w-3 text-green-400 mt-0.5 flex-shrink-0" />
                      {kpi}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benchmarks Section - COMPACT */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <span className="inline-block bg-amber-500/15 text-amber-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              {currentLanguage === 'de' ? 'Benchmarks' : 'Benchmarks'}
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-2">
              {currentLanguage === 'de'
                ? 'Wer das heute schon so macht'
                : 'Who Already Does This Today'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {benchmarks.map((benchmark, index) => (
              <div 
                key={index}
                className="bg-surface-sunken rounded-xl p-4 border border-hairline hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-5 w-5 text-brand-400" />
                  <h3 className="text-base font-bold text-ink">{benchmark.company}</h3>
                </div>
                <p className="text-ink-muted text-sm mb-2">
                  <strong>{currentLanguage === 'de' ? 'Fakt:' : 'Fact:'}</strong> {benchmark.fact}
                </p>
                <p className="text-ink-muted text-xs">
                  <strong>{currentLanguage === 'de' ? 'Muster:' : 'Pattern:'}</strong> {benchmark.pattern}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Start Section - COMPACT */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <span className="inline-block bg-brand/15 border border-teal-500/30 text-brand-300 px-3 py-1.5 rounded-full text-xs font-medium mb-3">
              {currentLanguage === 'de' ? 'So starten Sie' : 'How to Start'}
            </span>
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-2">
              {currentLanguage === 'de'
                ? 'KI-Value-Engine in 90 Tagen'
                : 'AI Value Engine in 90 Days'}
            </h2>
            <p className="text-base text-ink-faint max-w-[62ch]">
              {currentLanguage === 'de'
                ? 'Ein Modell, das im Kundentempo funktioniert.'
                : 'A model that works at the customer\'s pace.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            <div className="bg-surface-raised/50 backdrop-blur border border-hairline rounded-xl p-5 text-center">
              <div className="w-10 h-10 bg-brand/15 rounded-full flex items-center justify-center mx-auto mb-3">
                <Briefcase className="h-5 w-5 text-brand-400" />
              </div>
              <div className="text-brand-400 text-xs font-medium mb-1">
                {currentLanguage === 'de' ? 'Schritt 1' : 'Step 1'}
              </div>
              <h3 className="text-base font-bold text-white mb-2">
                {currentLanguage === 'de' ? 'Portfolio-Setup' : 'Portfolio Setup'}
              </h3>
              <p className="text-ink-faint text-xs">
                {currentLanguage === 'de'
                  ? 'Use-Case-Intake, Scoring, Portfolio-Buckets'
                  : 'Use case intake, scoring, portfolio buckets'}
              </p>
              <p className="text-ink-faint text-xs mt-2">2-4 {currentLanguage === 'de' ? 'Wochen' : 'weeks'}</p>
            </div>

            <div className="bg-surface-raised/50 backdrop-blur border border-hairline rounded-xl p-5 text-center">
              <div className="w-10 h-10 bg-brand/15 rounded-full flex items-center justify-center mx-auto mb-3">
                <LineChart className="h-5 w-5 text-brand-400" />
              </div>
              <div className="text-brand-400 text-xs font-medium mb-1">
                {currentLanguage === 'de' ? 'Schritt 2' : 'Step 2'}
              </div>
              <h3 className="text-base font-bold text-white mb-2">
                {currentLanguage === 'de' ? 'Value-Dashboard v1' : 'Value Dashboard v1'}
              </h3>
              <p className="text-ink-faint text-xs">
                {currentLanguage === 'de'
                  ? '12 Kern-KPIs: Readiness, Adoption, Impact'
                  : '12 core KPIs: Readiness, Adoption, Impact'}
              </p>
              <p className="text-ink-faint text-xs mt-2">{currentLanguage === 'de' ? 'Parallel' : 'Parallel'}</p>
            </div>

            <div className="bg-surface-raised/50 backdrop-blur border border-hairline rounded-xl p-5 text-center">
              <div className="w-10 h-10 bg-brand/15 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="h-5 w-5 text-brand-400" />
              </div>
              <div className="text-brand-400 text-xs font-medium mb-1">
                {currentLanguage === 'de' ? 'Schritt 3' : 'Step 3'}
              </div>
              <h3 className="text-base font-bold text-white mb-2">
                {currentLanguage === 'de' ? 'Operating Rhythm' : 'Operating Rhythm'}
              </h3>
              <p className="text-ink-faint text-xs">
                {currentLanguage === 'de'
                  ? '60 Min. Value-Board mit Stop/Fix/Scale'
                  : '60 min. Value Board with Stop/Fix/Scale'}
              </p>
              <p className="text-ink-faint text-xs mt-2">{currentLanguage === 'de' ? 'Ab Woche 4' : 'From week 4'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - COMPACT */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-teal-600 to-cyan-600">
        <div className="container mx-auto px-4">
          <div className="max-w-[62ch] text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-3 py-1.5 mb-4">
              <Award className="h-4 w-4 text-white" />
              <span className="text-white text-xs font-medium">
                {currentLanguage === 'de' ? 'Kostenlos & unverbindlich' : 'Free & non-binding'}
              </span>
            </div>

            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-4">
              {currentLanguage === 'de'
                ? 'Bereit für messbare KI-Transformation?'
                : 'Ready for Measurable AI Transformation?'}
            </h2>

            <p className="text-lg text-white/90 mb-6">
              {currentLanguage === 'de'
                ? 'Starten Sie mit dem kostenlosen KI-Value-Check und erfahren Sie, wo Ihr Unternehmen steht.'
                : 'Start with the free AI Value Check and find out where your company stands.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                size="lg"
                onClick={handleValueCheck}
                className="bg-surface text-brand-400 hover:bg-surface-raised px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                data-testid="cta-value-check-btn"
              >
                {currentLanguage === 'de' ? 'KI-Value-Check starten' : 'Start AI Value Check'}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={handleContact}
                className="border-hairline-strong text-white hover:bg-white/[0.07] px-6 py-3 font-semibold rounded-lg transition-all duration-300"
                data-testid="cta-contact-btn"
              >
                <HeartHandshake className="mr-2 h-4 w-4" />
                {currentLanguage === 'de' ? 'Executive Briefing buchen' : 'Book Executive Briefing'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4">
        <AuthorBox />
      </div>

      <Footer />
    </div>
  );
};

export default KIValueEngine;
